import React from 'react';
import { Edit, Mail } from 'lucide-react';
import { Modal } from '../../ui/Modal';
import { Badge } from '../../ui/Badge';
import { Button } from '../../ui/Button';
import { DetailField } from '../course/DetailField';
import { PracticeHistory } from './PracticeHistory';
import { practiceDeliveryModes } from '../../../data/practiceOptions';
import { formatEuro } from '../../../utils/currency';
import { formatCourseDate, productLabel } from '../../../utils/trainingCourses';
import { billingStepLabel, isInPresence, requesterKindLabel, requesterName, practiceTotal, rateTypeLabel } from '../../../utils/practices';
import { Practice } from '../../../types/practice';
interface PracticeDetailModalProps {
  practice: Practice | null;
  onClose: () => void;
  onEdit: (practice: Practice) => void;
  onEmail: (practice: Practice) => void;
}
export const practiceStatusVariant = {
  Positivo: 'success',
  'In valutazione': 'warning',
  Negativo: 'danger'
} as const;
export function PracticeDetailModal({
  practice,
  onClose,
  onEdit,
  onEmail
}: PracticeDetailModalProps) {
  const sectionTitle = 'text-sm font-semibold text-seform-navy mb-4';
  const total = practice ? practiceTotal(practice) : undefined;
  return <Modal isOpen={Boolean(practice)} onClose={onClose} title="Dettaglio pratica" size="2xl" data-id="element-1331" data-mp="3nro8zgwto9my">
      {practice && <div className="space-y-6" data-id="element-1332" data-mp="3ersqss4unedf">
          <div className="flex items-start justify-between gap-4 pb-5 border-b border-slate-100" data-id="element-1333" data-mp="3eqq1upr37m05">
            <div data-id="element-1334" data-mp="3iufnb6cgtngy">
              <h3 className="text-xl font-bold text-seform-navy" data-id="element-1335" data-mp="3iuxsish3vc10">
                {requesterName(practice)}
              </h3>
              <p className="text-sm text-slate-500 mt-0.5" data-id="element-1336" data-mp="34vpnvk26bbpf">
                {requesterKindLabel(practice)}
              </p>
            </div>
            <Badge variant={practiceStatusVariant[practice.status]} data-id="element-1337" data-mp="131ytd1oaojt7">
              {practice.status}
            </Badge>
          </div>

          {practice.clientType !== 'individuo' && <section data-id="element-1338" data-mp="16kpaizqzxafj">
              <h4 className={sectionTitle} data-id="element-1339" data-mp="16l7haqbab71p">
                Partecipanti al corso · {practice.attendees?.length || 0}
              </h4>
              {practice.attendees && practice.attendees.length > 0 ? <ul className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-2" data-id="element-1340" data-mp="wmrpel4fntr5">
                  {practice.attendees.map(a => <li key={a.id} className="min-w-0" data-id="element-1341" data-mp="rp9nvfsqwgt6">
                      <p className="text-sm font-medium text-slate-800 truncate" data-id="element-1342" data-mp="1p9cdncpm3pqh">
                        {a.firstName} {a.lastName}
                      </p>
                      <p className="text-xs text-slate-500 truncate" data-id="element-1343" data-mp="1fkkp7ejrfnkb">{a.email}</p>
                    </li>)}
                </ul> : <p className="text-sm text-slate-500" data-id="element-1344" data-mp="1ajomv5f249h0">Nessun partecipante indicato.</p>}
            </section>}

          <section data-id="element-1345" data-mp="23aos1jp4jja9">
            <h4 className={sectionTitle} data-id="element-1346" data-mp="23a6qq8nluevh">Informazioni corso</h4>
            <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-4" data-id="element-1347" data-mp="2bj2non2xiabd">
              <DetailField label="Macrocategoria" data-id="element-1348" data-mp="2bl5nfytvvhta">{practice.category}</DetailField>
              <DetailField label="Codice prodotto + Tipologia corso" data-id="element-1349" data-mp="2fl6a26r4yzbf">
                {productLabel(practice.productCode)}
              </DetailField>
              <DetailField label="Tariffa" data-id="element-1350" data-mp="2pikd94o1trls">{rateTypeLabel(practice)}</DetailField>
              {practice.rateType === 'oraria' ? <>
                  <DetailField label="Tariffa oraria" data-id="element-1351" data-mp="2uhopy5a6m7gv">
                    {practice.hourlyRate !== undefined && `${formatEuro(practice.hourlyRate)}/h`}
                  </DetailField>
                  <DetailField label="Monte ore corso" data-id="element-1352" data-mp="33cbqoo4whvms">
                    {practice.courseHours !== undefined && `${practice.courseHours} ore`}
                  </DetailField>
                </> : <>
                  <DetailField label="Costo singola iscrizione" data-id="element-1353" data-mp="3i8kp5ngyw4y5">
                    {practice.singleFee !== undefined && formatEuro(practice.singleFee)}
                  </DetailField>
                  <DetailField label="Numero corsisti" data-id="element-1354" data-mp="dtmdnhqq28ou">
                    {practice.participantsCount}
                  </DetailField>
                </>}
              <DetailField label="Importo complessivo senza IVA" data-id="element-1355" data-mp="njp53di73el2">
                {total !== undefined && formatEuro(total)}
              </DetailField>
              <DetailField label="Email cliente" data-id="element-1356" data-mp="3w0ah7364inse">{practice.clientEmail}</DetailField>
              <DetailField label="Provenienza" data-id="element-1357" data-mp="3r2idzd013f8w">{practice.source}</DetailField>
              <DetailField label="Data" data-id="element-1358" data-mp="8wdtjbm0juky">{formatCourseDate(practice.date)}</DetailField>
              {practice.status === 'Negativo' && <>
                  <DetailField label="Data della non accettazione" data-id="element-1359" data-mp="1kahmqfks1eoy">
                    {formatCourseDate(practice.rejectionDate)}
                  </DetailField>
                  <DetailField label="Motivazione della non accettazione" full data-id="element-1360" data-mp="1g8ct3gubwi2b">
                    {practice.rejectionReason}
                  </DetailField>
                </>}
            </dl>
          </section>

          {practice.status === 'Positivo' && <>
              <section className="pt-6 border-t border-slate-100" data-id="element-1361" data-mp="1dvsij5msdkzk">
                <h4 className={sectionTitle} data-id="element-1362" data-mp="1isiylsylcgkz">Programmazione corso</h4>
                {practice.deliveryMode ? <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-4" data-id="element-1363" data-mp="26jy600585o33">
                    <DetailField label="Tipo di corso" full data-id="element-1364" data-mp="2bi9dw5byloio">
                      {practiceDeliveryModes.find(m => m.value === practice.deliveryMode)?.label}
                    </DetailField>
                    <DetailField label="Codice Corso" data-id="element-1365" data-mp="2ph96tp6i8pkn">
                      <span className="font-mono" data-id="element-1366" data-mp="2pfo95qow8qik">{practice.courseCode}</span>
                    </DetailField>
                    <DetailField label="Stato del corso" data-id="element-1367" data-mp="2uabwhm85vevk">
                      {practice.courseStatus}
                    </DetailField>
                    {isInPresence(practice) ? <>
                        <DetailField label="Data inizio" data-id="element-1368" data-mp="2v3jf6b142tfm">
                          {formatCourseDate(practice.courseStartDate)}
                        </DetailField>
                        <DetailField label="Data fine" data-id="element-1369" data-mp="2q6szhot7xzuq">
                          {formatCourseDate(practice.courseEndDate)}
                        </DetailField>
                        <DetailField label="Dati del corso" full data-id="element-1370" data-mp="2gdu7ry2fdt6w">
                          {practice.courseDetails}
                        </DetailField>
                      </> : <DetailField label="Partecipanti" full data-id="element-1371" data-mp="27fi9raz8bt4f">
                        {practice.participants.length > 0 && <ul className="space-y-1" data-id="element-1372" data-mp="22h70b1cujkmq">
                            {practice.participants.map(p => <li key={p.id} data-id="element-1373" data-mp="cxz2h65jvioa">
                                {p.firstName} {p.lastName}
                                <span className="text-slate-500 font-normal" data-id="element-1374" data-mp="hx3cfz0atkfo">
                                  {' '}
                                  · {p.email} ·{' '}
                                  {p.credentialsSentAt ? 'credenziali inviate' : 'credenziali da inviare'}
                                </span>
                              </li>)}
                          </ul>}
                      </DetailField>}
                    <DetailField label="Programmazione" data-id="element-1375" data-mp="36w2k7ocx9ql">
                      {practice.schedulingConfirmed ? 'Confermata' : 'Da confermare'}
                    </DetailField>
                  </dl> : <p className="text-sm text-slate-500" data-id="element-1376" data-mp="37kko9kgi7ijf">
                    Corso non ancora programmato.
                  </p>}
              </section>
              <section className="pt-6 border-t border-slate-100" data-id="element-1377" data-mp="2516izqtlxol7">
                <dl className="grid grid-cols-1 sm:grid-cols-3 gap-x-8 gap-y-4" data-id="element-1378" data-mp="202d7g3xvs7no">
                  <DetailField label="Proforma" data-id="element-1379" data-mp="2eykm6oqisgvk">
                    {billingStepLabel(practice.proforma, 'Proformato')}
                  </DetailField>
                  <DetailField label="Fattura" data-id="element-1380" data-mp="29yg62ztixo5v">
                    {billingStepLabel(practice.invoice, 'Fatturato')}
                  </DetailField>
                  <DetailField label="Attestati" data-id="element-1381" data-mp="2j0z7rglkpuqj">{practice.certificates}</DetailField>
                </dl>
              </section>
            </>}

          <section className="pt-6 border-t border-slate-100" data-id="element-1382" data-mp="2xokdli9u2825">
            <h4 className={sectionTitle} data-id="element-1383" data-mp="2sradn8otie02">Storico e azioni</h4>
            <PracticeHistory history={practice.history} data-id="element-1384" data-mp="36q0b4pfjufoy" />
          </section>

          <div className="flex justify-end gap-3 pt-4 border-t border-slate-100" data-id="element-1385" data-mp="36nxnud5s70nx">
            <Button variant="flat" color="default" onClick={onClose} data-id="element-1386" data-mp="32q9ngulbkfon">
              Chiudi
            </Button>
            <Button variant="flat" color="secondary" startContent={<Mail className="h-4 w-4" data-id="element-1388" data-mp="3q9ppnromp5yl" />} onClick={() => onEmail(practice)} data-id="element-1387" data-mp="3lamio1p38pnz">
              Invia email
            </Button>
            <Button startContent={<Edit className="h-4 w-4" data-id="element-1390" data-mp="3h9kklo6wqjnl" />} onClick={() => onEdit(practice)} data-id="element-1389" data-mp="3hacf0vorqbca">
              Modifica
            </Button>
          </div>
        </div>}
    </Modal>;
}